import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserHistory, deleteHistory, deleteMultipleHistory, GenerationHistory, updateHistory } from '../../services/history';
import { getVideoModelConfig, getVideoTaskStatus, getVideoUrlFromTaskResult } from '../../services/video';
import Navbar from '../home/components/Navbar';
import Sidebar from '../home/components/Sidebar';

export default function HistoryPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState<GenerationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [taskProgress, setTaskProgress] = useState<Record<string, number>>({});
  /** 小窗播放：有值时弹出在线播放器，视频类用播放器，非视频用新标签打开 */
  const [playbackUrl, setPlaybackUrl] = useState<{ url: string; isVideo: boolean } | null>(null);

  /** 轮询时用当前列表，不整页拉取，避免整页刷新 */
  const historyRef = useRef<GenerationHistory[]>([]);
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  const categories = [
    { id: 'all', name: '全部', icon: 'ri-apps-line' },
    { id: 'ai-image', name: 'AI图像', icon: 'ri-image-line' },
    { id: 'style-transfer', name: '风格转换', icon: 'ri-palette-line' },
    { id: 'video', name: '视频生成', icon: 'ri-video-line' },
  ];

  useEffect(() => {
    if (user) {
      loadHistory();
    }
  }, [user, selectedCategory]);

  /** 是否为视频类（需用火山 API 补查任务状态） */
  const isVideoCategory = (category: string) =>
    category === 'video' || category === 'sora2-video' || category === 'veo31-video';

  /** 补查未保存 URL 的待处理视频任务，成功后写入 output_url，返回是否有任务完成 */
  const refreshPendingVideoTasks = async (list: GenerationHistory[]): Promise<boolean> => {
    const pending = list.filter(
      (item) =>
        item.task_id &&
        (item.status === 'pending' || item.status === 'processing') &&
        isVideoCategory(item.category)
    );
    if (pending.length === 0) return false;
    const config = await getVideoModelConfig();
    if (!config?.api_key) return false;
    
    let hasCompleted = false;
    for (const item of pending.slice(0, 10)) {
      try {
        const taskData = await getVideoTaskStatus(config, item.task_id!);
        if (!taskData) continue;
        if (taskData.status === 'succeeded') {
          hasCompleted = true;
          const videoUrl = getVideoUrlFromTaskResult(taskData);
          if (videoUrl) {
            await updateHistory(item.id, { status: 'succeeded', output_url: videoUrl });
            setHistory((prev) =>
              prev.map((h) =>
                h.id === item.id ? { ...h, status: 'succeeded', output_url: videoUrl } : h
              )
            );
          }
        } else if (taskData.status === 'failed' || taskData.status === 'expired') {
          hasCompleted = true;
          await updateHistory(item.id, { status: taskData.status });
          setHistory((prev) =>
            prev.map((h) => (h.id === item.id ? { ...h, status: taskData.status! } : h))
          );
        }
      } catch {
        // 单条失败不影响其他
      }
    }
    return hasCompleted;
  };

  // 只轮询“正在生成”的视频，完成后仅更新该条状态，不整页刷新
  useEffect(() => {
    if (!user) return;
    
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let isPolling = true;
    
    const pollPendingTasks = async () => {
      if (!isPolling) return;
      const currentList = historyRef.current;
      const hasPending = currentList.some(
        (item) =>
          item.task_id &&
          (item.status === 'pending' || item.status === 'processing') &&
          isVideoCategory(item.category)
      );
      if (!hasPending) {
        isPolling = false;
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      // 只更新正在生成的那条状态，不拉整页、不 setHistory(整页)
      await refreshPendingVideoTasks(currentList);
    };
    
    const startPollingTimeout = setTimeout(() => {
      pollPendingTasks();
      intervalId = setInterval(() => {
        if (isPolling) pollPendingTasks();
      }, 5000);
    }, 2000);
    
    return () => {
      isPolling = false;
      clearTimeout(startPollingTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, selectedCategory]);

  const loadHistory = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    const categoryFilter =
      selectedCategory === 'all'
        ? undefined
        : selectedCategory === 'video'
          ? ['video', 'sora2-video', 'veo31-video']
          : selectedCategory;
    const result = await getUserHistory(user.id, categoryFilter);
    if (result.error) {
      setHistory([]);
      setLoadError(result.error);
      setLoading(false);
      return;
    }
    const data = result.history || [];
    setHistory(data);
    // 只更新正在生成的那条状态，不整页再刷一次
    await refreshPendingVideoTasks(data);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这条记录吗？')) return;
    
    const result = await deleteHistory(id);
    if (result.success) {
      setHistory(history.filter(item => item.id !== id));
    } else {
      alert(result.error || '删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!confirm(`确定要删除选中的 ${selectedItems.size} 条记录吗？`)) return;

    const result = await deleteMultipleHistory(Array.from(selectedItems));
    if (result.success) {
      setHistory(history.filter(item => !selectedItems.has(item.id)));
      setSelectedItems(new Set());
    } else {
      alert(result.error || '批量删除失败');
    }
  };

  const toggleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === history.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(history.map(item => item.id)));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoryName = (category: string) => {
    return categories.find(c => c.id === category)?.name || category;
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-lock-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">请先登录</h2>
          <p className="text-gray-600 mb-6">登录后即可查看您的历史记录</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-emerald-600 transition-all whitespace-nowrap"
          >
            立即登录
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeMenu="历史记录" onMenuChange={(menu) => {}} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Navbar />
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto main-content-padding">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">历史记录</h1>
                <p className="text-gray-600">
                  查看和管理您的所有创作记录。更完整的分类归档与标签检索请前往
                  <button
                    type="button"
                    onClick={() => window.REACT_APP_NAVIGATE?.('/materials')}
                    className="text-teal-600 hover:underline ml-1 cursor-pointer"
                  >
                    素材空间
                  </button>
                </p>
              </div>
              <div className="flex items-center gap-3">
                {selectedItems.size > 0 && (
                  <button
                    onClick={handleBatchDelete}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-2"></i>
                    删除选中 ({selectedItems.size})
                  </button>
                )}
                <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-1 rounded ${viewMode === 'grid' ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-grid-line"></i>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1 rounded ${viewMode === 'list' ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'} transition-colors cursor-pointer`}
                  >
                    <i className="ri-list-check"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <i className={`${category.icon} mr-2`}></i>
                  {category.name}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">加载中...</p>
                </div>
              </div>
            ) : loadError ? (
              <div className="text-center py-20">
                <i className="ri-error-warning-line text-6xl text-red-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">历史记录加载失败</h3>
                <p className="text-gray-600 mb-6">{loadError}</p>
                <button
                  onClick={loadHistory}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  重试
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">暂无历史记录</h3>
                <p className="text-gray-600">开始创作后，您的作品将显示在这里</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-200"
                  >
                    <div
                      className="relative cursor-pointer group"
                      onClick={() => {
                        if (item.output_url) {
                          if (isVideoCategory(item.category)) {
                            setPlaybackUrl({ url: item.output_url, isVideo: true });
                          } else {
                            window.open(item.output_url, '_blank', 'noopener,noreferrer');
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (item.output_url && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          if (isVideoCategory(item.category)) {
                            setPlaybackUrl({ url: item.output_url, isVideo: true });
                          } else {
                            window.open(item.output_url, '_blank', 'noopener,noreferrer');
                          }
                        }
                      }}
                      role={item.output_url ? 'button' : undefined}
                      tabIndex={item.output_url ? 0 : undefined}
                      title={item.output_url ? (isVideoCategory(item.category) ? '点击小窗播放' : '点击在新标签页打开') : undefined}
                    >
                      <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => toggleSelectItem(item.id)}
                          className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                        />
                      </div>
                      {isVideoCategory(item.category) && item.output_url ? (
                        <>
                          <video
                            src={item.output_url}
                            controls
                            playsInline
                            className="w-full h-48 object-cover bg-black pointer-events-none"
                            preload="metadata"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none">
                            <span className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium bg-black/60 px-4 py-2 rounded-lg">
                              点击小窗播放
                            </span>
                          </div>
                        </>
                      ) : item.thumbnail_url || (item.output_url && !isVideoCategory(item.category)) ? (
                        <img
                          src={item.thumbnail_url || item.output_url}
                          alt={item.prompt || '生成内容'}
                          className="w-full h-48 object-cover"
                        />
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                          {isVideoCategory(item.category) ? (
                            item.output_url ? (
                              <span className="flex flex-col items-center gap-2 text-teal-600 group-hover:text-teal-700">
                                <i className="ri-video-line text-4xl"></i>
                                <span className="text-sm">点击在新标签页打开视频</span>
                              </span>
                            ) : (
                              <span className="flex flex-col items-center gap-2 text-gray-400">
                                <i className="ri-video-line text-4xl"></i>
                                <span className="text-sm">视频生成中或暂无链接</span>
                              </span>
                            )
                          ) : (
                            <i className="ri-image-line text-4xl text-gray-400"></i>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                        {item.prompt || '无描述'}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span>{formatDate(item.created_at)}</span>
                        {item.credits_used && (
                          <span className="text-amber-600 font-medium">
                            <i className="ri-coin-line mr-1"></i>
                            {item.credits_used}
                          </span>
                        )}
                      </div>
                      {item.task_id && (item.status === 'pending' || item.status === 'processing') && taskProgress[item.task_id] && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>生成进度</span>
                            <span>{taskProgress[item.task_id]}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-teal-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${taskProgress[item.task_id]}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        {item.output_url && (
                          isVideoCategory(item.category) ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setPlaybackUrl({ url: item.output_url!, isVideo: true }); }}
                              className="flex-1 px-3 py-2 bg-teal-50 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-100 transition-colors text-center whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-play-circle-line mr-1"></i>
                              播放
                            </button>
                          ) : (
                            <a
                              href={item.output_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 px-3 py-2 bg-teal-50 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-100 transition-colors text-center whitespace-nowrap"
                            >
                              查看
                            </a>
                          )
                        )}
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left">
                          <input
                            type="checkbox"
                            checked={history.length > 0 && selectedItems.size === history.length}
                            onChange={toggleSelectAll}
                            className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">预览</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">类型</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">描述</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">积分</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">创建时间</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {history.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleSelectItem(item.id)}
                              className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-6 py-4">
                            {isVideoCategory(item.category) && item.output_url ? (
                              <video
                                src={item.output_url}
                                controls
                                playsInline
                                className="w-16 h-16 object-cover rounded-lg bg-black"
                                preload="metadata"
                              />
                            ) : item.thumbnail_url || (item.output_url && !isVideoCategory(item.category)) ? (
                              <img
                                src={item.thumbnail_url || item.output_url}
                                alt="预览"
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                {isVideoCategory(item.category) ? (
                                  <i className="ri-video-line text-gray-400"></i>
                                ) : (
                                  <i className="ri-image-line text-gray-400"></i>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium whitespace-nowrap">
                              {getCategoryName(item.category)}
                            </span>
                          </td>
                          <td className="px-6 py-4 max-w-md">
                            <p className="text-sm text-gray-700 line-clamp-2">
                              {item.prompt || '无描述'}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            {item.credits_used && (
                              <span className="text-amber-600 font-medium text-sm whitespace-nowrap">
                                <i className="ri-coin-line mr-1"></i>
                                {item.credits_used}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(item.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            {item.task_id && (item.status === 'pending' || item.status === 'processing') && taskProgress[item.task_id] && (
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>生成进度</span>
                                  <span>{taskProgress[item.task_id]}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-gradient-to-r from-teal-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${taskProgress[item.task_id]}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2">
                              {item.output_url && (
                                isVideoCategory(item.category) ? (
                                  <button
                                    type="button"
                                    onClick={() => setPlaybackUrl({ url: item.output_url!, isVideo: true })}
                                    className="px-3 py-1 bg-teal-50 text-teal-600 rounded-lg text-sm hover:bg-teal-100 transition-colors whitespace-nowrap cursor-pointer"
                                  >
                                    <i className="ri-play-circle-line mr-1"></i>
                                    播放
                                  </button>
                                ) : (
                                  <a
                                    href={item.output_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1 bg-teal-50 text-teal-600 rounded-lg text-sm hover:bg-teal-100 transition-colors whitespace-nowrap"
                                  >
                                    查看
                                  </a>
                                )
                              )}
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="px-3 py-1 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                删除
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 小窗播放器：视频在线播放，避免直接下载 */}
            {playbackUrl && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                onClick={() => setPlaybackUrl(null)}
              >
                <div
                  className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">
                      {playbackUrl.isVideo ? '视频播放' : '预览'}
                    </span>
                    <div className="flex items-center gap-2">
                      {playbackUrl.isVideo && (
                        <a
                          href={playbackUrl.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-teal-600 hover:text-teal-700 px-2 py-1"
                        >
                          在新标签页打开
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setPlaybackUrl(null)}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-600 cursor-pointer"
                        aria-label="关闭"
                      >
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0 flex items-center justify-center bg-black">
                    {playbackUrl.isVideo ? (
                      <video
                        src={playbackUrl.url}
                        controls
                        autoPlay
                        playsInline
                        className="w-full h-full max-h-[calc(90vh-52px)] object-contain"
                      />
                    ) : (
                      <img
                        src={playbackUrl.url}
                        alt="预览"
                        className="max-w-full max-h-[calc(90vh-52px)] object-contain"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
